import { useEffect, useState } from 'react';
import { useContextSelector as useCS } from 'use-context-selector';
import { Controller, FormContainer, useFormContext } from 'react-hook-form-mui';
import { Skeleton, Tooltip } from '@mui/material';
import { AvatarCheckIcon } from 'assets';
import { GlobalConfigContext } from 'domain/globalConfig/GlobalConfigContext';
import { globalConfigSelectors } from 'domain/globalConfig/selectors';
import { RolesEnum } from 'infrastructure/enums/RolesEnum';
import {
  IProfileSettingsFormFields,
  useUpdateProfileSettings,
} from 'view/pages/profile/hooks/useUpdateProfileSettings';
import { useGetProfileAvatars } from 'view/pages/profile/hooks/useGetProfileAvatars';
import { Button, Input, PhoneInput, Spinner } from 'view/components';
/* import Select from 'view/components/Select'; */
import TestSelect from 'view/pages/profile/TestPage/Select';
import * as CS from 'view/pages/profile/commonStyled';
import { LocationAutocomplete } from 'view/pages/profile/components';
import * as S from './styled';

const genderOptions = [
  { id: 1, name: 'Male' },
  { id: 2, name: 'Female' },
  { id: 3, name: 'Non-Binary' },
  { id: 4, name: 'Prefer not to say' },
  { id: 5, name: 'Other' },
];

const ethnicityOptions = [
  { id: 1, name: 'Asian' },
  { id: 2, name: 'Black, including African American' },
  { id: 3, name: 'Hispanic, Latino/a or Latinx' },
  { id: 4, name: 'Middle Eastern or North African' },
  { id: 5, name: 'Native American or Alaska Native' },
  { id: 6, name: 'Pacific Islander, including Native Hawaiian' },
  { id: 7, name: 'White or Caucasian' },
  { id: 8, name: 'Prefer not to say' },
];

const disabilityStatusOptions = [
  { id: 1, name: 'Yes' },
  { id: 2, name: 'No' },
  { id: 3, name: 'Prefer not to say' },
];

const veteranStatusOptions = [
  { id: 1, name: 'Yes' },
  { id: 2, name: 'No' },
  { id: 3, name: 'Prefer not to say' },
];

const ProfileFormFields = ({ isLoading, error }) => {
  const {
    role: { name: roleName },
  } = useCS(GlobalConfigContext, globalConfigSelectors.userData);

  const {
    clearErrors,
    setError,
    watch,
    setValue,
    control,
    formState: { errors },
  } = useFormContext<IProfileSettingsFormFields>();

  useEffect(() => {
    if (!error) {
      clearErrors(['contactEmail', 'contactPhone', 'firstName', 'lastName']);
      return;
    }

    if (error.contactEmail) {
      setError('contactEmail', { type: 'custom', message: error.contactEmail });
      const el = document.querySelector('input[name="contactEmail"]');
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    if (error.contactPhone) {
      setError('contactPhone', { type: 'custom', message: error.contactPhone });
      const el = document.querySelector('input[name="contactPhone"]');
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [error, setError, clearErrors]);

  const firstNameValue = watch('firstName') ?? '';
  const lastNameValue = watch('lastName') ?? '';
  const contactEmailValue = watch('contactEmail') ?? '';
  const contactPhoneValue = watch('contactPhone') ?? '';
  const currentLocationValue = watch('currentLocation') ?? null;

  const isDisabled =
    !!errors?.contactEmail ||
    !!errors?.contactPhone ||
    !!errors?.firstName ||
    !!errors?.lastName ||
    !firstNameValue ||
    !lastNameValue ||
    !contactEmailValue ||
    !contactPhoneValue ||
    isLoading;

  return (
    <>
      <Input
        label="First Name *"
        name="firstName"
        type="text"
        placeholder="Enter your first name"
        marginbottom={16}
      />
      <Input
        label="Last Name *"
        name="lastName"
        type="text"
        placeholder="Enter your last name"
        marginbottom={16}
      />
      <S.InputWithCheckboxWrapper>
        <Input
          label="Contact Email *"
          name="contactEmail"
          type="text"
          placeholder="e.g. youremail@email.com"
          marginbottom={8}
        />
        <S.CheckboxWithText>
          <S.CustomCheckbox
            name="showEmail"
            checked={watch('showEmail')}
            onChange={(e) => {
              return setValue('showEmail', e.target.checked);
            }}
            disabled={!contactEmailValue}
          />
          <p>
            {roleName === RolesEnum.JOB_SEEKER
              ? 'Share with recruiters'
              : 'Use this email for candidate communication'}
          </p>
        </S.CheckboxWithText>
      </S.InputWithCheckboxWrapper>
      <S.InputWithCheckboxWrapper>
        <Controller
          name="contactPhone"
          control={control}
          render={({ field, fieldState }) => {
            return (
              <PhoneInput
                label="Contact Phone *"
                value={field.value}
                onChange={field.onChange}
                error={fieldState.error?.message}
                marginbottom={8}
              />
            );
          }}
        />
        <S.CheckboxWithText>
          <S.CustomCheckbox
            name="showPhone"
            checked={watch('showPhone')}
            onChange={(e) => {
              return setValue('showPhone', e.target.checked);
            }}
            disabled={!contactPhoneValue}
          />
          <p>
            {roleName === RolesEnum.JOB_SEEKER
              ? 'Share with recruiters'
              : 'Use this phone for candidate communication'}
          </p>
        </S.CheckboxWithText>
      </S.InputWithCheckboxWrapper>
      <LocationAutocomplete setValue={setValue} currentLocationValue={currentLocationValue} />
      <S.Separator />
      <S.DemographicInformationBlock>
        <S.DemographicInformationBlockTitle>
          Demographic Information
        </S.DemographicInformationBlockTitle>
        <S.DemographicInformationBlockDesc>
          This information needs to help us ensure fair and inclusive hiring practices. This
          information is completely confidential, will never be shared with other users.
        </S.DemographicInformationBlockDesc>
      </S.DemographicInformationBlock>
      <Controller
        name="gender"
        control={control}
        render={({ field }) => {
          return (
            <TestSelect
              {...field}
              label="Gender"
              options={genderOptions}
              selectedValue={field.value}
              onChange={(selected) => {
                return field.onChange(selected);
              }}
            />
          );
        }}
      />
      <Controller
        name="ethnicity"
        control={control}
        render={({ field }) => {
          return (
            <TestSelect
              {...field}
              label="Ethnicity"
              options={ethnicityOptions}
              selectedValue={field.value}
              onChange={(selected) => {
                return field.onChange(selected);
              }}
            />
          );
        }}
      />
      <Controller
        name="disabilityStatus"
        control={control}
        render={({ field }) => {
          return (
            <TestSelect
              {...field}
              label="Disability Status"
              options={disabilityStatusOptions}
              selectedValue={field.value}
              onChange={(selected) => {
                return field.onChange(selected);
              }}
            />
          );
        }}
      />
      <Controller
        name="veteranStatus"
        control={control}
        render={({ field }) => {
          return (
            <TestSelect
              {...field}
              label="Veteran Status"
              options={veteranStatusOptions}
              selectedValue={field.value}
              onChange={(selected) => {
                return field.onChange(selected);
              }}
            />
          );
        }}
      />
      <Tooltip
        title={
          isDisabled && !isLoading
            ? 'To save your profile, please fill in all required fields or fix all errors on the Profile Settings page.'
            : ''
        }
        arrow
        placement="top"
      >
        <div>
          <Button
            variant="contained"
            type="submit"
            text="Save"
            endIcon={isLoading ? <Spinner disabled={isLoading} variant="contained" /> : null}
            disabled={isDisabled}
          />
        </div>
      </Tooltip>
    </>
  );
};

const ProfileSettings = () => {
  const {
    firstName,
    lastName,
    contactEmail,
    contactPhone,
    photo,
    demographicInfo: { gender, ethnicity, disabilityStatus, veteranStatus },
    privacySettings: { showEmail, showPhone },
    currentLocation,
  } = useCS(GlobalConfigContext, globalConfigSelectors.userData);

  const { data: profileAvatars, isLoading: isProfileAvatarsLoading } = useGetProfileAvatars();
  const {
    isLoading: isUpdateProfileSettingsLoading,
    updateProfileSettings,
    error,
  } = useUpdateProfileSettings();

  const [selectedAvatarId, setSelectedAvatarId] = useState(0);

  const defaultValues = {
    firstName: firstName || '',
    lastName: lastName || '',
    contactEmail: contactEmail || '',
    showEmail: showEmail || false,
    contactPhone: contactPhone || '',
    showPhone: showPhone || false,
    currentLocation: currentLocation || null,
    gender: gender
      ? genderOptions.find((option) => {
          return option.name === gender;
        })
      : null,
    ethnicity: ethnicity
      ? ethnicityOptions.find((option) => {
          return option.name === ethnicity;
        })
      : null,
    disabilityStatus: disabilityStatus
      ? disabilityStatusOptions.find((option) => {
          return option.name === disabilityStatus;
        })
      : null,
    veteranStatus: veteranStatus
      ? veteranStatusOptions.find((option) => {
          return option.name === veteranStatus;
        })
      : null,
  };

  useEffect(() => {
    if (!isProfileAvatarsLoading && profileAvatars) {
      if (photo) {
        const avatarElem = profileAvatars.find((el: any) => {
          return el.id === photo?.id;
        });
        setSelectedAvatarId(avatarElem.id);
      } else setSelectedAvatarId(profileAvatars?.[0]?.id);
    }
  }, [isProfileAvatarsLoading, profileAvatars]);

  const onSubmit = (formData: IProfileSettingsFormFields) => {
    updateProfileSettings(formData, selectedAvatarId);
  };

  return (
    <CS.ProfileMainWrapper>
      <S.PersonalInfoBlock>
        <S.PersonalInfoBlockTitle>Personal Information</S.PersonalInfoBlockTitle>
        <S.PersonalInfoBlockDesc>
          {
            'Your first and last name are private. \n Your email and phone will only be shared if you choose.'
          }
        </S.PersonalInfoBlockDesc>
      </S.PersonalInfoBlock>
      <S.ProfileAvatarBlock>
        <S.ProfileAvatarBlockTitle>Profile Avatar</S.ProfileAvatarBlockTitle>
        {profileAvatars && !isProfileAvatarsLoading ? (
          <S.ProfileAvatarBlockAvatars>
            {profileAvatars.map((avatar: any) => {
              const { id, link } = avatar;
              const selected = id === selectedAvatarId;
              return (
                <S.AvatarItem
                  key={id}
                  onClick={() => {
                    return setSelectedAvatarId(id);
                  }}
                  selected={selected}
                >
                  {selected ? (
                    <AvatarCheckIcon style={{ position: 'absolute', top: '-3px', right: '-3px' }} />
                  ) : null}
                  <S.ProfileAvatarImg src={link} alt="avatar" selected={selected} />
                </S.AvatarItem>
              );
            })}
          </S.ProfileAvatarBlockAvatars>
        ) : (
          <Skeleton
            variant="rectangular"
            animation="wave"
            width="100%"
            height={56}
            sx={{ borderRadius: '14px' }}
          />
        )}
      </S.ProfileAvatarBlock>
      <CS.FormWrapper>
        <FormContainer
          defaultValues={defaultValues}
          onSuccess={onSubmit}
          key={JSON.stringify(defaultValues)}
        >
          <ProfileFormFields isLoading={isUpdateProfileSettingsLoading} error={error} />
        </FormContainer>
      </CS.FormWrapper>
    </CS.ProfileMainWrapper>
  );
};

export default ProfileSettings;
